import { c as _c } from "react/compiler-runtime";
import type { GraphQLRecentTransaction } from '@sola/schema/dotcom/supporters/schema.generated.ts';
import { type GraphQLCommitment, GraphQLCommitmentType } from '@sola/schema/dotcom/supporters/schema.generated.ts';
import { useSupporterQuery } from '@sola/supporter-sdk/hooks/base.ts';
import { commitmentsQuery, recentTransactionsQuery } from '@sola/supporter-sdk/queries.ts';
import type { AvatarCommitment } from '../../atoms/commitment-avatar/commitment-avatar.tsx';
import { DEFAULT_RECENT_ACTIVITY_ITEM_LIMIT } from '../../types/recent-activity.ts';
enum TransactionStage {
  Supporter = 'Supporter',
  Commitment = 'Commitment',
  Payment = 'Payment',
  Wallet = 'Wallet',
}
const successfulStatus = 'Successful';
export type RecentActivityPanelTransactionRow = {
  /** Stable React key. */
  id: string;
  /** Latest `completed` timestamp within the group (ISO string). */
  completedAt?: string;
  /** Latest `completed` timestamp within the group (ISO string). */
  requestedAt: string;
  /** Sum of all payment amounts across every transaction in the group. */
  total: number;
  /** True when the transaction is flagged unsuccessful. */
  hasIssue: boolean;
  /** True when the transaction is flagged as a payment issue. */
  hasPaymentIssue: boolean;
  // /** Commitment or commitment-shaped item data from the transaction. */
  commitments: AvatarCommitment[];
};
export const getTransactionIssue = (transaction: GraphQLRecentTransaction): string | undefined => {
  if (transaction.status !== successfulStatus) {
    return transaction.error?.step ?? 'Supporter';
  }
};
export const getRecentActivityPanelTransactionRows = (transactions: GraphQLRecentTransaction[] = [], commitments: GraphQLCommitment[] = [], limit = DEFAULT_RECENT_ACTIVITY_ITEM_LIMIT): RecentActivityPanelTransactionRow[] => {
  const {
    commitmentsMap,
    childCommitmentsMap
  } = commitments.reduce((acc, commitment) => {
    if (commitment.commitmentType === GraphQLCommitmentType.ChildSponsorship && commitment.participant?.globalId) {
      acc.childCommitmentsMap.set(commitment.participant.globalId, commitment);
    }
    acc.commitmentsMap.set(commitment.globalId, commitment);
    return acc;
  }, {
    commitmentsMap: new Map<string, GraphQLCommitment>(),
    childCommitmentsMap: new Map<string, GraphQLCommitment>()
  });
  const transactionRows: RecentActivityPanelTransactionRow[] = [];
  for (const transaction of transactions) {
    // The panel only shows pending/problem transactions with enough data to render.
    if (!transaction.original || transaction.status === successfulStatus) {
      continue;
    }
    const issue = getTransactionIssue(transaction);
    const {
      total,
      avatarCommitments
    } = transaction.original.items.reduce((acc, item) => {
      acc.total += item.currentRate;
      if (item.commitmentId && commitmentsMap.has(item.commitmentId)) {
        const commitment = commitmentsMap.get(item.commitmentId);
        if (commitment) {
          acc.avatarCommitments.push({
            ...commitment,
            commitmentType: item.commitmentType
          });
        }
      } else if (item.commitmentType === GraphQLCommitmentType.ChildSponsorship && item.beneficiaryGlobalId && childCommitmentsMap.has(item.beneficiaryGlobalId)) {
        // New child sponsorships don't have a commitmentId
        const commitment = childCommitmentsMap.get(item.beneficiaryGlobalId);
        if (commitment) {
          acc.avatarCommitments.push({
            ...commitment,
            commitmentType: item.commitmentType
          });
        }
      } else {
        acc.avatarCommitments.push({
          internalId: `${item.fundCode}${item.beneficiaryGlobalId}`,
          commitmentType: item.commitmentType,
          fund: {
            code: item.fundCode,
            icon: item.fundIcon
          },
          ...(item.commitmentType === GraphQLCommitmentType.ChildSponsorship && {
            participant: {
              globalId: item.beneficiaryGlobalId,
              photoUrl: item.beneficiaryPhotoUrl
            }
          })
        });
      }
      return acc;
    }, {
      total: 0,
      avatarCommitments: [] as AvatarCommitment[]
    });
    transactionRows.push({
      id: transaction.transactionId,
      completedAt: transaction.completedAt ?? undefined,
      requestedAt: transaction.original.requestedAt,
      total,
      hasIssue: !!issue,
      hasPaymentIssue: issue === TransactionStage.Payment || issue === TransactionStage.Wallet,
      commitments: avatarCommitments
    });
  }
  const rows = transactionRows.toSorted((a, b) => Date.parse(b.requestedAt) - Date.parse(a.requestedAt)).slice(0, limit);
  return rows;
};
export const useComponentRecentActivityPanelTransactions = () => {
  const $ = _c(9);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [recentTransactionsQuery, commitmentsQuery];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    data: t1,
    isPending
  } = useSupporterQuery(t0);
  const [t2, t3] = t1;
  let t4;
  if ($[1] !== t2) {
    t4 = t2 === undefined ? [] : t2;
    $[1] = t2;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const transactions = t4;
  let t5;
  if ($[3] !== t3) {
    t5 = t3 === undefined ? [] : t3;
    $[3] = t3;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const commitments = t5;
  let t6;
  if ($[5] !== commitments || $[6] !== isPending || $[7] !== transactions) {
    t6 = {
      transactions,
      commitments,
      isLoading: isPending
    };
    $[5] = commitments;
    $[6] = isPending;
    $[7] = transactions;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  return t6;
};